import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Table, Image, Button, Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import _ from "lodash";
import cx from "classnames";
import moment from "moment";

import { fetchUser, activateUser, fetchUsers } from "actions/users";

import InfiniteScroll from "components/ui/InfiniteScroll";

import styles from "./users.module.scss";
import { isAccountSuspended } from "components/features/Util/Util";
import { useParams} from "react-router-dom";

const initUserFilters = {
  type: "ALL",
  sort: "first_name",
  order: "ASC",
};

export default function Users({
  account,
  type,
  query,
  sort,
  order,
  setTotal,
  setLoading,
  permissionData
}) {
  const dispatch = useDispatch();
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);

  const [filters, setFilters] = useState({ ...initUserFilters, type: type });
  const [initialRender, setInitialRender] = useState(true);

  const user = useSelector((state) => _.get(state, "users.user"));
  const users = useSelector((state) => _.get(state, "users.items"));
  const loading = useSelector((state) => _.get(state, "users.loading"));
  const params = useParams();

  const currentPage = useSelector((state) =>
    _.get(state, "users.pagination.currentPage")
  );

  const totalPages = useSelector((state) =>
    _.get(state, "users.pagination.totalPages")
  );

  const totalRecordsCount = useSelector((state) =>
    _.get(state, "users.pagination.totalRecordsCount")
  );

  const activatingUser = useSelector((state) =>
    _.get(state, "users.activatingUser")
  );

  const creatingUser = useSelector((state) =>
    _.get(state, "users.creatingUser")
  );

  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (!initialRender && creatingUser) {
      if (!creatingUser.creating && creatingUser.success) {
        setFilters({ ...filters });
      }
    }
  }, [creatingUser]);

  useEffect(() => {
    if (!initialRender && activatingUser) {
      if (!activatingUser.activating && activatingUser.success) {
        toast.success(
          ` ${user.enabled ? "Activated" : "Deactivated "} ${user.firstName}`
        );
        setFilters({ ...filters});
      }
    }
  }, [activatingUser]);

  useEffect(() => {
    if (!activatingUser.activating && !creatingUser.creating) {
      setTotal(totalRecordsCount);
    }
  }, [totalRecordsCount]);

  useEffect(() => {
    if (query) {
      query = query.trim();
    }

    setPage(0);
    setFilters({ ...filters, query: query });
  }, [query]);

  useEffect(() => {
    setPage(0);
    setFilters({ ...filters, type: type });
  }, [type]);

  useEffect(() => {
    setFilters({ ...filters });
  }, [page]);

  useEffect(() => {
    setPage(0);
    setFilters({ ...filters, sort: sort});
  }, [sort]);

  useEffect(() => {
    setPage(0);
    setFilters({ ...filters, order: order});
  }, [order]);

  useEffect(() => {
    if (!loading && account) {
      if (query) {
        query = query.trim();
      }

      dispatch(
        fetchUsers({
          page: page,
          limit: limit,
          filters: { ...filters, account: account.id, query: query },
        })
      );
    }
  }, [filters]);

  useEffect(() => {
    if (!loading && initialRender) {
      setFilters({ ...filters });
    }
  }, [account]);

  setTimeout(
    function () {
      setInitialRender(false);
    },
    [3000]
  );

  const onActivateUser = (id, enabled) => {
    let type = filters.type;
    dispatch(activateUser({ id, enabled, type }));
  };

  const handleClick = (user) => {
    
    dispatch(fetchUser({ id: user.id }));
    history.push({
      pathname: `/${params.product}/accounts/${account.id}/user_management/${user.id}`,
      state: { accountId: account.id, id: user.id },
    });
  };

  const renderStatus = (user) => {
    if (user) {
      if (user.enabled) {
        return (
          <>
            <span>
              <Icon
                name="dot circle"
                className={cx(styles.icon, styles.active)}
              />
              Active
            </span>
            <div className={cx(styles.timeago)}>
              {user.lastActivityDate
                ? moment.utc(user.lastActivityDate).format('DD-MMM-YYYY')
                : ""}
            </div>
          </>
        );
      } else {
        return (
          <>
            <span>
              <Icon
                name="dot circle"
                className={cx(styles.icon, styles.disabled)}
              />
              Disabled
            </span>
            <div className={cx(styles.timeago)}>
              {user.lastActivityDate
                ? moment.utc(user.lastActivityDate).format('DD-MMM-YYYY')
                : ""}
            </div>
          </>
        );
      }
    }

    return <></>;
  };

  return (
    <>
      <InfiniteScroll
        loading={loading}
        currentPage={currentPage}
        totalPages={totalPages}
        pagination={true}
        setPage={setPage}
      >
        <Table singleLine className={cx(styles.table)}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={4} className={cx(styles.header)}>
                NAME
              </Table.HeaderCell>
              <Table.HeaderCell width={4} className={cx(styles.header)}>
                EMAIL
              </Table.HeaderCell>
              <Table.HeaderCell width={3} className={cx(styles.header)}>
                ROLE
              </Table.HeaderCell>
              <Table.HeaderCell width={2} className={cx(styles.header)}>
                STATUS
              </Table.HeaderCell>
              <Table.HeaderCell
                width={3}
                className={cx(styles.header)}
              ></Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body className={cx(styles.body)}>
            {!loading && users.length === 0 && (
              <>
                <Table.Row>
                  <Table.Cell colSpan="5" className={cx(styles.cell)}>
                    <div className={cx(styles.noData)}>
                      {filters.type === "ALL" && <> No Users </>}
                      {filters.type === "active" && <> No Active Users </>}
                      {filters.type === "disabled" && <> No Disabled Users </>}
                    </div>
                  </Table.Cell>
                </Table.Row>
              </>
            )}

            {loading && users.length === 0 && (
              <>
                <Table.Row>
                  <Table.Cell colSpan="5" className={cx(styles.cell)}>
                    <div className={cx(styles.noData)}>
                      Loading
                      <Icon name="sync" loading className={cx(styles.icon)} />
                    </div>
                  </Table.Cell>
                </Table.Row>
              </>
            )}

            {users.length !== 0 && (
              <>
                {users.map((item, idx) => (
                  <>
                    <Table.Row key={idx}>
                      <Table.Cell width={4} className={cx(styles.cell)}>
                        <div>
                          <Image
                            src={
                              item.icon
                                ? item.icon.thumbnail
                                : "https://react.semantic-ui.com/images/avatar/large/steve.jpg"
                            }
                            className={cx(styles.image)}
                          />
                        </div>
                        <div
                          className={cx(styles.text, styles.pointer)}
                          onClick={() => handleClick(item)}
                        >
                          {item.name}
                        </div>
                      </Table.Cell>
                      <Table.Cell width={4} className={cx(styles.cell)}>
                        <div className={cx(styles.text)}> {item.email} </div>
                      </Table.Cell>
                      <Table.Cell width={3} className={cx(styles.cell)}>
                        <div className={cx(styles.text)}>
                          {_.get(item, 'roles[0].name', '')}
                        </div>
                      </Table.Cell>
                      <Table.Cell width={2} className={cx(styles.cell)}>
                        <div className={cx(styles.text, styles.status)}>
                          {renderStatus(item)}
                        </div>
                      </Table.Cell>
                      <Table.Cell
                        width={3}
                        textAlign="center"
                        className={cx(styles.cell)}
                      >
                        {item.enabled ? (
                          <Button
                            content="Deactivate"
                            onClick={() => onActivateUser(item.id, false)}
                            className={cx(styles.btn, styles.deactivate)}
                            disabled={isAccountSuspended(account) || !_.get(permissionData, 'user.write', false)}
                          />
                        ) : (
                          <Button
                            content="Activate"
                            onClick={() => onActivateUser(item.id, true)}
                            className={cx(styles.btn, styles.activate)}
                            disabled={isAccountSuspended(account) || !_.get(permissionData, 'user.write', false)}
                          />
                        )}
                      </Table.Cell>
                    </Table.Row>
                  </>
                ))}
              </>
            )}
          </Table.Body>
        </Table>
      </InfiniteScroll>
    </>
  );
}
